/**
 * Версии актива.
 *
 * Текущая версия помечена, остальные остаются доступными: смысл версии в том,
 * что к ней можно вернуться. Удалить версию нельзя — можно только сделать
 * текущей другую.
 */

import { CheckIcon, DownloadIcon, ExternalLinkIcon, HistoryIcon, Loader2Icon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { AssetVersionDto, AttachmentDto } from '@project-os/contracts';
import { useCreateAssetVersion, useMakeVersionCurrent } from '@/api/assets';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { formatBytes } from './AttachmentsSection';

/** Значение «внешняя ссылка» в выборе содержимого версии. */
const EXTERNAL = '__external__';

export function VersionsSection({
  projectId,
  itemId,
  versions,
  attachments,
  canEdit,
}: {
  projectId: string;
  itemId: string;
  versions: AssetVersionDto[];
  /** Файлы элемента: из них выбирается содержимое новой версии. */
  attachments: AttachmentDto[];
  canEdit: boolean;
}) {
  const [addOpen, setAddOpen] = useState(false);
  const makeCurrent = useMakeVersionCurrent(projectId, itemId);

  const promote = async (version: AssetVersionDto) => {
    try {
      await makeCurrent.mutateAsync(version.id);
      toast.success(`Текущая версия: ${version.versionLabel}`);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось сменить версию');
    }
  };

  return (
    <div className="space-y-3">
      {versions.length === 0 ? (
        <p className="text-xs leading-relaxed text-muted-foreground">
          Версий пока нет. Версия фиксирует, каким результат был в определённый момент: приложите
          файл или укажите ссылку на внешнее хранилище.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {versions.map((version) => (
            <li
              key={version.id}
              className={cn(
                'surface-card border p-2.5',
                version.isCurrent ? 'border-primary/50' : 'border-border/50',
              )}
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{version.versionLabel}</span>
                {version.isCurrent ? (
                  <span className="flex items-center gap-1 rounded-md bg-primary-soft px-1.5 py-0.5 text-2xs font-medium text-primary">
                    <CheckIcon className="size-3" />
                    текущая
                  </span>
                ) : null}

                <div className="flex-1" />

                {version.attachment ? (
                  <Button variant="ghost" size="icon" className="size-7" asChild>
                    <a
                      href={version.attachment.downloadUrl}
                      download={version.attachment.filename}
                      title="Скачать"
                    >
                      <DownloadIcon className="size-3.5" />
                      <span className="sr-only">Скачать версию {version.versionLabel}</span>
                    </a>
                  </Button>
                ) : null}

                {version.externalUrl ? (
                  <Button variant="ghost" size="icon" className="size-7" asChild>
                    <a
                      href={version.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Открыть внешнюю ссылку"
                    >
                      <ExternalLinkIcon className="size-3.5" />
                      <span className="sr-only">Открыть версию {version.versionLabel}</span>
                    </a>
                  </Button>
                ) : null}

                {canEdit && !version.isCurrent ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7"
                    disabled={makeCurrent.isPending}
                    onClick={() => void promote(version)}
                  >
                    Сделать текущей
                  </Button>
                ) : null}
              </div>

              {version.changeSummary ? (
                <p className="mt-1 text-xs leading-relaxed">{version.changeSummary}</p>
              ) : null}

              <p className="mt-1 flex flex-wrap gap-x-2 text-2xs text-muted-foreground">
                <span>{version.createdBy.name}</span>
                <span>{formatDateTime(version.createdAt)}</span>
                {version.attachment ? (
                  <span>
                    {version.attachment.filename} · {formatBytes(version.attachment.sizeBytes)}
                  </span>
                ) : null}
              </p>
            </li>
          ))}
        </ul>
      )}

      {canEdit ? (
        <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
          <HistoryIcon />
          Добавить версию
        </Button>
      ) : null}

      <AddVersionDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        projectId={projectId}
        itemId={itemId}
        attachments={attachments}
        existingLabels={versions.map((version) => version.versionLabel)}
      />
    </div>
  );
}

function AddVersionDialog({
  open,
  onOpenChange,
  projectId,
  itemId,
  attachments,
  existingLabels,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  itemId: string;
  attachments: AttachmentDto[];
  existingLabels: string[];
}) {
  const [versionLabel, setVersionLabel] = useState('');
  const [source, setSource] = useState<string>(attachments[0]?.id ?? EXTERNAL);
  const [externalUrl, setExternalUrl] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  const create = useCreateAssetVersion(projectId, itemId);

  const reset = () => {
    setVersionLabel('');
    setSource(attachments[0]?.id ?? EXTERNAL);
    setExternalUrl('');
    setChangeSummary('');
  };

  const duplicate = existingLabels.includes(versionLabel.trim());

  const submit = async () => {
    try {
      await create.mutateAsync({
        versionLabel: versionLabel.trim(),
        changeSummary: changeSummary.trim() || null,
        attachmentId: source === EXTERNAL ? null : source,
        externalUrl: source === EXTERNAL ? externalUrl.trim() : null,
        makeCurrent: true,
      });
      toast.success('Версия добавлена');
      reset();
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось добавить версию');
    }
  };

  const ready =
    versionLabel.trim().length > 0 &&
    !duplicate &&
    (source !== EXTERNAL || externalUrl.trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Новая версия</DialogTitle>
          <DialogDescription>
            Прежняя версия останется в истории и будет доступна для скачивания.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="version-label">Обозначение</Label>
            <Input
              id="version-label"
              value={versionLabel}
              onChange={(event) => setVersionLabel(event.target.value)}
              placeholder="например, v2 или 2026-08-24"
              autoFocus
            />
            {duplicate ? (
              <p className="text-2xs text-tone-danger">Такая версия у актива уже есть</p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="version-source">Содержимое</Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger id="version-source" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {attachments.map((attachment) => (
                  <SelectItem key={attachment.id} value={attachment.id}>
                    {attachment.filename}
                  </SelectItem>
                ))}
                <SelectItem value={EXTERNAL}>Внешняя ссылка</SelectItem>
              </SelectContent>
            </Select>
            {attachments.length === 0 ? (
              <p className="text-2xs text-muted-foreground">
                Чтобы версия хранила файл, сначала приложите его на вкладке «Файлы».
              </p>
            ) : null}
          </div>

          {source === EXTERNAL ? (
            <div className="space-y-1.5">
              <Label htmlFor="version-url">Ссылка</Label>
              <Input
                id="version-url"
                value={externalUrl}
                onChange={(event) => setExternalUrl(event.target.value)}
                placeholder="https://github.com/..."
              />
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="version-summary">Что изменилось</Label>
            <Textarea
              id="version-summary"
              value={changeSummary}
              onChange={(event) => setChangeSummary(event.target.value)}
              rows={3}
              placeholder="Необязательно, но через месяц это единственное, что объяснит разницу"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button disabled={!ready || create.isPending} onClick={() => void submit()}>
            {create.isPending ? <Loader2Icon className="animate-spin" /> : null}
            Добавить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
